
import Vue, { PropType } from 'vue'
import { Block } from '@interfaces/BlockTypes'

import scssBreakpoints from '~/assets/scss/common/breakpoints-export.scss'

// Website-specific breakpoints from assets/scss/common/variables.scss
const BREAKPOINTS = {
  sm: parseInt(scssBreakpoints.sm, 10),
  md: parseInt(scssBreakpoints.md, 10),
  lg: parseInt(scssBreakpoints.lg, 10),
}

// Max logos per breakpoint (ensuring multiple of columns)
const MAX_LOGOS = {
  xs: 8,
  sm: 10,
  md: 16, 
  lg: 20,
}

const COLUMNS = {
  xs: 3,
  sm: 5,
  md: 8,
  lg: 10,
}

export default Vue.extend({
  name: 'CommonEducationBrandsBlock',
  props: {
    block: {
      type: Object as PropType<Block>,
      required: true,
    },
  },

  data() {
    return {
      carouselSlides: 3,
      loadSafetyTimeout: null as any,
      loadedImagesCount: 0,
      displayedLogos: [] as any[],
      bufferLogos: [] as any[],
    }
  },

  computed: {
    svgStyle() {
      const block: Block = this.block
      return block?.introIcons?.whiteLogo ? { filter: 'invert(1)' } : {}
    },

    titleText(): string {
      const title = this.block?.title
      if (typeof title === 'string') return title
      return title?.text || ''
    },

    titleColor(): string {
      const title = this.block?.title
      if (typeof title === 'object' && title?.color) return title.color
      return ''
    },
    
    currentColumns(): number {
      const w = this.$vuetify.breakpoint.width || 1200
      if (w < BREAKPOINTS.sm) return COLUMNS.xs
      if (w < BREAKPOINTS.md) return COLUMNS.sm
      if (w < BREAKPOINTS.lg) return COLUMNS.md
      return COLUMNS.lg
    },
    
    maxLogosLimit(): number {
      const w = this.$vuetify.breakpoint.width || 1200
      if (w < BREAKPOINTS.sm) return MAX_LOGOS.xs
      if (w < BREAKPOINTS.md) return MAX_LOGOS.sm
      if (w < BREAKPOINTS.lg) return MAX_LOGOS.md
      return MAX_LOGOS.lg
    },
    
    filteredLogos(): any[] {
      const allLogos = this.block?.introIcons?.logo || []
      const columns = this.currentColumns
      const limit = this.maxLogosLimit
      
      // Calculate how many logos to show to have equal rows
      let targetCount = Math.min(allLogos.length, limit)
      targetCount = Math.floor(targetCount / columns) * columns
      
      if (targetCount <= 0) return []
      
      const seed = this.getStringHash(this.titleText) || 42
      const selectedIndices = this.getRandomIndices(allLogos.length, targetCount, seed)
      
      return selectedIndices
        .sort((a: number, b: number) => a - b)
        .map((i: number) => allLogos[i])
    },

    logosWrapperHeight(): string {
      const logos = this.filteredLogos
      const columns = this.currentColumns
      if (!logos.length || !columns) return '72px'
      
      const rows = Math.ceil(logos.length / columns)
      return `${rows * (72 - 16)}px`
    },
  },

  watch: {
    filteredLogos: {
      handler(newVal) {
        // If it's the first load, show immediately
        if (this.displayedLogos.length === 0) {
          this.displayedLogos = [...newVal]
          return
        }

        // Otherwise, load in buffer first
        this.bufferLogos = [...newVal]
        this.loadedImagesCount = 0
        
        // Safety timeout to swap anyway if things get stuck
        if (this.loadSafetyTimeout) clearTimeout(this.loadSafetyTimeout)
        this.loadSafetyTimeout = setTimeout(() => {
          this.swapLogos()
        }, 1200)
      },
      immediate: true,
    },
  },
  
  beforeDestroy() {
    if (this.loadSafetyTimeout) clearTimeout(this.loadSafetyTimeout)
  },
  
  methods: {

    getCardNumber(index: number) {
      return Number(index) + 1
    },

    onLogoLoad() {
      this.loadedImagesCount++
      if (this.loadedImagesCount >= this.bufferLogos.length) {
        this.swapLogos()
      }
    },

    swapLogos() {
      if (this.bufferLogos.length > 0) {
        this.displayedLogos = [...this.bufferLogos]
        this.bufferLogos = []
      }
      if (this.loadSafetyTimeout) clearTimeout(this.loadSafetyTimeout)
    },
    
    getRandomIndices(total: number, count: number, seed: number): number[] {
      const available = Array.from({ length: total }, (_, i) => i)
      
      // Seeded PRNG
      let currentSeed = seed
      const nextRandom = () => {
        currentSeed = (currentSeed * 1103515245 + 12345) & 0x7fffffff
        return currentSeed / 2147483648 // Ensures [0, 1)
      }

      for (let i = available.length - 1; i > 0; i--) {
        const j = Math.floor(nextRandom() * (i + 1))
        const temp = available[i]
        available[i] = available[j]
        available[j] = temp
      }
      
      return available.slice(0, count)
    },

    getStringHash(str: string): number {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash
      }
      return Math.abs(hash)
    },
  },
})
